import { Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { renderShipmentCalculatedValue } from '../../components/shipment-calculated-value/render-shipment-calculated-value'
import formatDuration from '../../helpers/date-time/format-duration'
import { getShipmentDisplayCalculated } from '../../helpers/entities/shipment/get-shipment-display-calculated'
import formatIntegerBySpace from '../../helpers/formatters/format-integer-by-space'
import renderDatetime from '../../helpers/tables/render-datetime'
import PrintJob from '../../types/api/print-job'
import Lacquers from '../shipments/components/cells/lacquers/lacquers'
import ActionsCell from './components/actions-cell/actions-cell'
import {
    formatLayerLabel,
    getConsumptionSavedByUsername,
    getRowMileageDisplay,
    hasSavedMaterialConsumptions,
    roundAmountKm,
} from './helpers/consumption-helpers'

export const getColumns = (onSuccess: () => void): ColumnsType<PrintJob> => [
    {
        title: 'Печатник',
        width: 120,
        render: (_, r) => r.print_job_executors?.map(x => x.user?.username).join(', ') || '—',
    },
    {
        title: 'Дата и время начала печати',
        width: 120,
        render: (_, r) =>
            r.print_job_executors?.[0]?.start
                ? renderDatetime(r.print_job_executors[0].start)
                : '—',
    },
    {
        title: 'Дата и время окончания печати',
        width: 110,
        render: (_, r) =>
            r.print_job_executors?.[0]?.end ? renderDatetime(r.print_job_executors[0].end) : '—',
    },
    {
        dataIndex: ['shipment', 'additional_jobs'],
        title: 'Доп. работы',
        width: 100,
        render: (_, r) => r.shipment?.additional_jobs?.map(x => x.name).join(', ') || '—',
    },
    {
        title: 'Размер этикетки',
        width: 90,
        render: (_, r) => {
            const stamp = r.shipment?.stamp
            if (!stamp?.width || !stamp?.length) return '—'
            return `${stamp.width}x${stamp.length}`
        },
    },
    {
        title: 'Штамп',
        width: 74,
        render: (_, r) => r.shipment?.stamp?.number || '—',
    },
    {
        dataIndex: 'length',
        title: 'Рассчитанная длина',
        width: 90,
        render: (v, r) =>
            renderShipmentCalculatedValue(r.shipment, calculated =>
                Math.round((calculated?.materialLength || 0) * 1000) / 1000
            ),
    },
    {
        title: 'Тираж',
        width: 70,
        render: (_, r) => {
            const copies =
                r.shipment?.shipment_positions?.reduce((acc, x) => acc + x.copies, 0) || 0

            return formatIntegerBySpace(copies)
        },
    },
    {
        title: 'Цветность',
        width: 140,
        children: [
            {
                title: 'CMYK',
                dataIndex: ['shipment', 'cmyk'],
                width: 70,
                render: (_, r) => r.shipment?.cmyk || 0,
            },
            {
                title: 'Пантоны',
                dataIndex: ['shipment', 'pantones'],
                width: 70,
                render: (_, r) => r.shipment?.pantones || 0,
            },
            {
                title: 'Белила',
                dataIndex: ['shipment', 'whitewash'],
                width: 70,
                render: (_, r) => r.shipment?.whitewash || 0,
            },
            {
                title: 'Лак',
                dataIndex: ['shipment', 'lacquers'],
                width: 30,
                render: (_, r) => (r.shipment ? <Lacquers data={r.shipment} /> : '—'),
            },
        ],
    },
    {
        title: 'Станки фактические',
        width: 110,
        render: (_, r) => r.print_job_executors?.map(x => x.machine?.name).join(', ') || '—',
    },
    {
        dataIndex: ['shipment', 'id'],
        title: 'Номер отгрузки',
        width: 70,
    },
    {
        dataIndex: ['shipment', 'client', 'name'],
        title: 'Заказчик',
        width: 120,
    },
    {
        title: 'Фактическое время печати',
        width: 110,
        render: (_, r) => {
            const start = r.print_job_executors?.[0]?.start
            const end = r.print_job_executors?.[0]?.end

            if (!start || !end) return '—'

            const minutes = Math.max(0, dayjs(end).diff(dayjs(start), 'minute', true))
            return formatDuration(minutes)
        },
    },
    {
        title: 'Проверил',
        width: 120,
        render: (_, r) => getConsumptionSavedByUsername(r) || '—',
    },
    {
        title: 'Материалы',
        width: 200,
        render: (_, r) => {
            if (!r.material_consumptions?.length) return '—'

            return (
                <>
                    {r.material_consumptions.map(consumption => (
                        <div key={consumption.documentId} style={{ marginBottom: 4 }}>
                            <div>{consumption.material?.material_type?.name || '—'}</div>
                            <div>
                                {consumption.material?.width ?? '—'} ш. {consumption.amount ?? '—'} км.{' '}
                                {formatLayerLabel(consumption.layerNumber)}
                            </div>
                        </div>
                    ))}
                </>
            )
        },
    },
    {
        dataIndex: 'actualFloods',
        title: 'Кол-во ручьев фактическое',
        width: 100,
        render: v => v ?? ' ',
    },
    {
        title: 'Пробеги станков',
        width: 140,
        render: (_, r) => getRowMileageDisplay(r),
    },
    {
        title: 'Рассчитанный остаток',
        width: 200,
        render: (_, r) => {
            if (!hasSavedMaterialConsumptions(r)) return '—'

            const items = (r.material_consumptions || []).filter(
                consumption => consumption.calculatedRemainder != null
            )

            if (!items.length) return '—'

            return (
                <>
                    {items.map(consumption => (
                        <div key={consumption.documentId} style={{ marginBottom: 4 }}>
                            {consumption.material?.width ?? '—'} ш.{' '}
                            {roundAmountKm(consumption.calculatedRemainder)} км
                        </div>
                    ))}
                </>
            )
        },
    },
    {
        title: 'Разница во времени',
        width: 110,
        render: (_, r) => {
            const start = r.print_job_executors?.[0]?.start
            const end = r.print_job_executors?.[0]?.end
            const calculated = getShipmentDisplayCalculated(r.shipment)?.commonTime

            if (!start || !end || calculated == null) return '—'

            const actual = Math.max(0, dayjs(end).diff(dayjs(start), 'minute', true))
            const diff = actual - calculated
            const prefix = diff > 0 ? '-' : '+'

            return (
                <Typography.Text type={prefix === '+' ? 'success' : 'danger'}>
                    {prefix}
                    {formatDuration(Math.abs(diff))}
                </Typography.Text>
            )
        },
    },
    {
        title: 'Действия',
        width: 90,
        fixed: 'right',
        render: (_, r) => <ActionsCell printJob={r} onSuccess={onSuccess} />,
    },
]
